import PropTypes from 'prop-types';

import { Location } from 'history';
import { Config } from '../../../common/types/config';
import { BackendState, BackendStateWithData } from '../../types';
import { CancelationReasonDTO } from '../../types/dto/CancelationReasonDTO';
import { DealersDTO } from '../../types/dto/DealersDTO';
import { GeolocationsDTO } from '../../types/dto/GeolocationsDTO';
import { PMPoliciesDTO } from '../../types/dto/PMPoliciesDTO';
import { PolicyConsentDTO } from '../../types/dto/PolicyConsentDTO';
import { PolicyDTO } from '../../types/dto/PolicyDTO';
import { SeriesSegmentsDTO } from '../../types/dto/SeriesSegmentsDTO';
import { VehicleBrandDTO } from '../../types/dto/VehicleBrandDTO';
import { VehicleModelDTO } from '../../types/dto/VehicleModelDTO';
import { VehicleVersionDTO } from '../../types/dto/VehicleVersionDTO';
import { LookupValueDTOShape } from '../../types/prop-types/common';

//////////////////////////////////////////////////////////////////////////////////////////
// Misc shapes
//////////////////////////////////////////////////////////////////////////////////////////
export interface PingData {
    readonly pingResult?: boolean;
}

export interface PolicyConsentsStatusData {
    readonly consentsSaved?: boolean;
}

export interface PoliciesData {
    readonly policies?: PolicyDTO;
}

//////////////////////////////////////////////////////////////////////////////////////////
// Redux state shapes
//////////////////////////////////////////////////////////////////////////////////////////
export interface PingState extends BackendStateWithData<PingData> { }

export interface ConfigState {
    config?: Config;
}

export interface MenuGoBackUrlState {
    location?: Location<any>;
}

export interface LogClientInfoState extends BackendState { }

export interface PolicyConsentsStatusState extends BackendStateWithData<PolicyConsentsStatusData> { }

export interface PoliciesState extends BackendStateWithData<PoliciesData> { }

export interface PMPoliciesState extends BackendStateWithData<PMPoliciesDTO | null> { }

export interface LookupValuesState extends BackendState {
    readonly marcas: VehicleBrandDTO[];
    readonly modelos: VehicleModelDTO[];
    readonly versoes: VehicleVersionDTO[];
    readonly dealers: DealersDTO[];
    readonly districts: string[];
    readonly cancelationReasons: CancelationReasonDTO[];
    readonly seriesSegments: SeriesSegmentsDTO[];
}

export const DealersDTOPropType = PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string,
    district: PropTypes.string,
});

export interface GetGeolocationsState extends BackendState {
    readonly geolocations: GeolocationsDTO[];
}

export interface AppState {
    readonly ping: PingState;
    readonly config: ConfigState;
    readonly menuGoBackUrl: MenuGoBackUrlState;
    readonly logClientInfo: LogClientInfoState;
    readonly selectedPolicyConsent: PolicyConsentDTO | null;
    readonly selectedPolicyConsentStatus: PolicyConsentsStatusState;
    readonly lookupValues: LookupValuesState;
    readonly getGeolocations: GetGeolocationsState;
    readonly policies: PoliciesState;
    readonly pmpolicies: PMPoliciesState;
}

//////////////////////////////////////////////////////////////////////////////////////////
// Action shapes
//////////////////////////////////////////////////////////////////////////////////////////

export const AppActionPropTypes = PropTypes.shape({
    pingRequest: PropTypes.func.isRequired,
    pingSuccess: PropTypes.func.isRequired,
    pingFailure: PropTypes.func.isRequired,
    logClientInfoRequest: PropTypes.func.isRequired,
    logClientInfoSuccess: PropTypes.func.isRequired,
    logClientInfoFailure: PropTypes.func.isRequired,
    selectPolicyConsent: PropTypes.func.isRequired,
    setPolicyConsentsRequest: PropTypes.func.isRequired,
    setPolicyConsentsSuccess: PropTypes.func.isRequired,
    setPolicyConsentsFailure: PropTypes.func.isRequired,
    loadLookupValuesSuccess: PropTypes.func.isRequired,
    loadLookupValuesFailure: PropTypes.func.isRequired,
    loadLookupValuesRequest: PropTypes.func.isRequired,
    getGeolocationsSuccess: PropTypes.func.isRequired,
    getGeolocationsFailure: PropTypes.func.isRequired,
    getGeolocationsRequest: PropTypes.func.isRequired,
    loadPoliciesRequest: PropTypes.func.isRequired,
    loadPoliciesSuccess: PropTypes.func.isRequired,
    loadPoliciesFailure: PropTypes.func.isRequired,
    loadPMPoliciesRequest: PropTypes.func.isRequired,
    loadPMPoliciesSuccess: PropTypes.func.isRequired,
    loadPMPoliciesFailure: PropTypes.func.isRequired,
    updateConfig: PropTypes.func.isRequired,
    menuGoBackUrl: PropTypes.func.isRequired,
});

//////////////////////////////////////////////////////////////////////////////////////////
// Api response shapes
//////////////////////////////////////////////////////////////////////////////////////////

export const VehicleBrandDTOShape = PropTypes.shape({
    ...LookupValueDTOShape,
    value: PropTypes.number.isRequired,
});
export const VehicleModelDTOShape = PropTypes.shape({
    vehicleBrand: PropTypes.number,
    value: PropTypes.number.isRequired,
});
export const GroupedVehicleModelDTOShape = PropTypes.shape({
    ...LookupValueDTOShape,
    options: PropTypes.arrayOf(VehicleModelDTOShape.isRequired).isRequired,
});
export const VehicleVersionDTOShape = PropTypes.shape({
    vehicleBrand: PropTypes.number,
    vehicleModel: PropTypes.number,
    value: PropTypes.number.isRequired,
});
export const GroupedVehicleVersionDTOShape = PropTypes.shape({
    ...LookupValueDTOShape,
    options: PropTypes.arrayOf(VehicleModelDTOShape.isRequired).isRequired,
});
